import type {GroupBase, StylesConfig, Theme} from 'react-select';
import type {MultiselectOption} from 'common/multiselect';
import type {
    ColorPalette,
    FontSizes,
    FontWeights,
    Radii,
    Sizes,
    ZIndices,
} from 'common/theme/theme.types';
import {remToPixels} from 'common/theme/utils';

export interface ThemeArguments {
    colors: ColorPalette;
    fontSizes: FontSizes;
    fontWeights: FontWeights;
    radii: Radii;
    sizes: Sizes;
    zIndices: ZIndices;
}

const generateCustomTheme = ({
    radii,
    colors,
    sizes,
}: Pick<ThemeArguments, 'radii' | 'colors' | 'sizes'>): Theme => {
    return {
        borderRadius: remToPixels(radii.md),
        colors: {
            primary: colors.primary.default,
            primary25: colors.primary[100],
            primary50: colors.primary[400],
            primary75: colors.primary[500],
            danger: colors.red.default,
            dangerLight: colors.red[100],
            neutral0: colors.white,
            neutral5: colors.gray[50],
            neutral10: colors.gray[100],
            neutral20: colors.gray[200],
            neutral30: colors.gray[300],
            neutral40: colors.gray[400],
            neutral50: colors.gray[500],
            neutral60: colors.gray[600],
            neutral70: colors.gray[700],
            neutral80: colors.gray[800],
            neutral90: colors.gray[900],
        },
        spacing: {
            baseUnit: remToPixels(sizes[1]),
            controlHeight: remToPixels(sizes[8]),
            menuGutter: remToPixels(sizes[2.5]),
        },
    };
};

const generateCustomStyles = ({
    colors,
    fontSizes,
    fontWeights,
    radii,
    sizes,
    zIndices,
}: ThemeArguments): StylesConfig<
    MultiselectOption,
    true,
    GroupBase<MultiselectOption>
> => {
    return {
        container: (base) => ({
            ...base,
            position: 'relative',
        }),
        control: (base, state) => {
            return {
                ...base,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                minHeight: sizes[10],
                paddingInlineStart: sizes[2],
                paddingInlineEnd: sizes[2],
                paddingTop: sizes[2],
                paddingBottom: sizes[2],
                borderColor: state.isFocused
                    ? colors.primary.default
                    : colors.gray['200'],
                borderWidth: '1px',
                boxShadow: 'none',
                '&:hover': {
                    borderColor: state.isFocused
                        ? colors.primary.default
                        : colors.gray[300],
                },
            };
        },
        indicatorsContainer: (base) => {
            return {
                ...base,
                marginLeft: sizes[2],
                '&>div': {
                    padding: 0,
                },
            };
        },
        valueContainer: () => {
            return {
                display: 'flex',
                flex: '1 1 0%',
                flexWrap: 'wrap',
                alignItems: 'center',
                paddingInlineStart: 0,
                paddingInlineEnd: 0,
                rowGap: sizes[2],
                columnGap: sizes[1],
            };
        },
        input: () => {
            return {
                appearance: 'none',
                display: 'flex',
                alignItems: 'center',
                flex: '1 1 0%',
                fontSize: fontSizes.base,
                padding: 0,
                margin: 0,
                minWidth: sizes[32],
                width: '-webkit-fill-available',
                minHeight: '1.375rem',
                outline: 'none',
                '&>::placeholder': {
                    color: colors.gray.dark,
                },
            };
        },
        placeholder: () => {
            return {
                display: 'none',
            };
        },
        multiValue: () => {
            return {
                display: 'inline-flex',
                alignItems: 'center',
                verticalAlign: 'top',
                backgroundColor: colors.navBlue,
                borderRadius: radii.full,
                paddingInlineStart: sizes[3],
                paddingInlineEnd: sizes[3],
                height: '1.375rem',
                paddingTop: sizes[0.5],
                paddingBottom: sizes[0.5],
                '&:hover': {
                    backgroundColor: colors.gray[200],
                },
            };
        },
        multiValueLabel: (base) => ({
            ...base,
            padding: 0,
            paddingInlineStart: 0,
            fontSize: fontSizes.sm,
            maxWidth: sizes[40],
        }),
        multiValueRemove: (base) => ({
            ...base,
            '&:hover': {
                backgroundColor: colors.gray[200],
                borderRadius: radii.full,
                color: colors.text.primary.light,
            },
        }),
        menu: (base) => {
            return {
                ...base,
                boxShadow: 'none',
                border: `1px solid ${colors.gray[200]}`,
                borderRadius: radii.md,
                paddingTop: sizes[1],
                paddingBottom: sizes[1],
                zIndex: zIndices.layer10,
            };
        },
        noOptionsMessage: (base) => {
            return {
                ...base,
                textAlign: 'left',
                fontSize: fontSizes.sm,
                color: colors.text.primary.light,
            };
        },
        groupHeading: (base) => {
            return {
                ...base,
                textTransform: 'none',
                fontWeight: fontWeights.semibold,
                fontSize: fontSizes.sm,
                color: colors.text.secondary.light,
                paddingInlineStart: sizes[2],
                paddingInlineEnd: sizes[2],
                paddingBottom: sizes[2],
            };
        },
        option: (base, state) => {
            return {
                ...base,
                fontSize: fontSizes.sm,
                minHeight: sizes[8],
                backgroundColor: state.isSelected
                    ? colors.navBlue
                    : 'transparent',
                color: colors.text.primary.light,
                '&:active': {
                    backgroundColor: colors.gray[100],
                },
                '&:hover': {
                    backgroundColor: state.isSelected
                        ? colors.gray[100]
                        : colors.gray[50],
                },
            };
        },
    };
};

export const generateCustomStylesAndTheme = ({
    colors,
    fontSizes,
    fontWeights,
    radii,
    sizes,
    zIndices,
}: ThemeArguments) => {
    const customTheme = generateCustomTheme({
        radii,
        colors,
        sizes,
    });
    const customStyles = generateCustomStyles({
        colors,
        fontSizes,
        fontWeights,
        radii,
        sizes,
        zIndices,
    });

    return {
        customStyles,
        customTheme,
    };
};

export const defaultSortByFn = (a: MultiselectOption, b: MultiselectOption) =>
    a.label.localeCompare(b.label);
